import { z } from 'zod';
import { LoginStart } from './session.js';

/**
 * Starting a channel connect: the broadcaster consent step (ADR 0036).
 *
 * The same same-origin `return_to` rule as signing in, and nothing else. What
 * is being connected is not a parameter: it is whichever Twitch channel the
 * person consents AS, which is the whole of the proof that it is theirs.
 */
export const ConnectStart = LoginStart.pick({ return_to: true });
export type ConnectStart = z.infer<typeof ConnectStart>;

/**
 * A channel's link to Twitch, as its owner sees it.
 *
 * Owner-only, and deliberately not part of `Channel`, which is what third
 * parties see: which scopes a streamer granted and whether their moderators
 * use tomo are the channel's own business.
 */
export const TwitchConnection = z.object({
  login: z.string(),
  /** Consented at the last connect. Empty for a channel connected before this was recorded. */
  scopes_granted: z.array(z.string()),
  /**
   * What tomo now asks for that this channel has not granted. Non-empty means
   * something tomo shows for this channel quietly does not arrive, and the
   * fix is to connect again.
   */
  scopes_missing: z.array(z.string()),
  connected_at: z.string().datetime().nullable(),
  /** Whether this channel's Twitch moderators may use tomo's mod tools (ADR 0050 §4). */
  mod_tools_enabled: z.boolean(),
  /**
   * Whether Twitch is delivering the events this channel's triggers pay out
   * on, as tomo last found it. Null until it has looked, which happens within
   * seconds of connecting. `revoked` means the broadcaster withdrew tomo on
   * Twitch; `failing` means a subscription could not be made and is retried.
   */
  events: z.object({
    status: z.enum(['receiving', 'failing', 'revoked']),
    since: z.string().datetime(),
  }).nullable(),
});
export type TwitchConnection = z.infer<typeof TwitchConnection>;

export const TwitchConnectionPatch = z.object({
  mod_tools_enabled: z.boolean(),
});
export type TwitchConnectionPatch = z.infer<typeof TwitchConnectionPatch>;
